import { FiCountry } from '../../contexts/LocaleContext';
import * as types from '../../types';
import { centsToDollars, centsToDollarsNumber, getLocalizedPricing } from './money';

export type RateUnit = 'month' | 'week' | 'day' | 'total';

interface SubscriptionRateVariant {
  unit: RateUnit;
  displayRate: string;
}

interface SubscriptionProduct {
  billingCadence: types.BillingCadence;
  displayedRate: string;
  /** @deprecated use `pricingInCents` instead */
  priceInCents: number;
  pricingInCents: types.PricingByCurrency;
  renewalMonths: number;
}

const DAYS_IN_A_MONTH = 30;
const DAYS_IN_A_WEEK = 7;

export function getPriceForRateUnit(
  subscriptionProduct: SubscriptionProduct,
  rateUnit: RateUnit,
  currency: types.FiCurrency,
  currencyFormatter: Intl.NumberFormat,
): SubscriptionRateVariant {
  let monthlyPriceInCents: number;
  const priceInCents = getLocalizedPricing(subscriptionProduct.pricingInCents, currency);
  if (subscriptionProduct.billingCadence === types.BillingCadence.Year) {
    monthlyPriceInCents = priceInCents / (subscriptionProduct.renewalMonths * 12);
  } else {
    monthlyPriceInCents = priceInCents / subscriptionProduct.renewalMonths;
  }

  let raw: number;
  switch (rateUnit) {
    case 'day':
      raw = Math.floor(monthlyPriceInCents / DAYS_IN_A_MONTH);
      // Daily
      return {
        unit: 'day',
        displayRate: currencyFormatter.format(centsToDollarsNumber(raw)),
      };

    case 'week':
      // TODO: fix this for Canada https://app.asana.com/1/553833434016848/project/1207101507783044/task/1211928492598062?focus=true
      // Weekly - round pennies to 0
      raw = Math.floor(((monthlyPriceInCents / DAYS_IN_A_MONTH) * DAYS_IN_A_WEEK) / 10) * 10;
      return {
        unit: 'week',
        displayRate: currencyFormatter.format(centsToDollarsNumber(raw)),
      };

    case 'month':
    default:
      // Monthly
      raw = Math.floor(monthlyPriceInCents);
      return {
        unit: 'month',
        // TODO: if we use this again, it needs to be updated to reflect the correct currency
        displayRate: subscriptionProduct.displayedRate,
      };
  }
}

function displayValue(value: number, fixed = 2): string {
  return `$${centsToDollars(value, fixed)}`;
}

/**
 * Appends currency suffix when cross-border billing occurs between US and Canada,
 * since both use the "$" symbol and this disambiguates the displayed price.
 */
export function displayUSDValueForRegion(
  region: FiCountry,
  currency: types.FiCurrency,
  value: string | null,
): string | null {
  if (value === null) return null;
  if (region === FiCountry.CA && currency === types.FiCurrency.USD) return `${value} USD`;
  if (region === FiCountry.US && currency === types.FiCurrency.CAD) return `${value} CAD`;
  return value;
}

const NANO_ELEVATED_COUPON_CODE_CENTS = 9900;

// One-off function to retrieve the *total* price of the nano subscription, including the activation fee
// Currently relies on the GB flag 'nano-elevated-promo-offer'.
export function getExperimentalTotalNanoPrice(subscriptionProduct: SubscriptionProduct): SubscriptionRateVariant {
  const displayRateValue = subscriptionProduct.priceInCents;

  const reducedPrice = displayRateValue - NANO_ELEVATED_COUPON_CODE_CENTS;

  return {
    unit: 'total',
    displayRate: displayValue(reducedPrice, 0),
  };
}
